// Story 11.10 (ADO-1961), the second PR: the margin info button beside a Margin label and the
// panel it opens (AC2, OQ-3; the designer's 3 Nov export). Shared by the PDP's detail strip and
// the order review's MARGIN header, the only two places it goes (never on cards or list rows).
// The content is `marginInfoContent.ts`'s; the button and panel are the design system's.

import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import {
  InfoButton,
  InfoLabel,
  InfoPanel,
  type InfoPanelContent,
  type InfoPanelSize,
} from '../design-system';
import { MARGIN_INFO_TITLE, pdpMarginInfo } from './marginInfoContent';
import { shownEntry } from './priceExplanation';
import { useProductMargin } from './ProductPrice';
import { realPricesEnabled } from './realPrices';
import { usePriceEntry } from './usePriceStore';

export interface MarginInfoProps {
  /** The label the button sits beside, as the host draws it. */
  readonly label: ReactNode;
  readonly content: InfoPanelContent;
  /** `default` from 1024px, `touch` on tablet, `sheet` on phones (MI-9). */
  readonly size: InfoPanelSize;
}

export function MarginInfo({ label, content, size }: MarginInfoProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const close = useCallback(() => setOpen(false), []);
  // A rotation or resize across a breakpoint swaps the panel for the sheet (or back); the open one
  // closes rather than reappear in a presentation nobody opened.
  const [openedAtSize, setOpenedAtSize] = useState(size);
  if (openedAtSize !== size) {
    setOpenedAtSize(size);
    setOpen(false);
  }
  return (
    <>
      <InfoLabel>
        {label}
        <InfoButton
          ref={buttonRef}
          label={MARGIN_INFO_TITLE}
          expanded={open}
          size={size === 'default' ? 'default' : 'touch'}
          controls={open && size !== 'sheet' ? panelId : undefined}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        />
      </InfoLabel>
      <InfoPanel
        open={open}
        onClose={close}
        anchorRef={buttonRef}
        size={size}
        id={panelId}
        {...content}
      />
    </>
  );
}

/**
 * The PDP's margin info for `sku`, or `null` for no button (MI-1, MI-3): only with real prices
 * on, where the store sees its margin and the quote is priced; then `pdpMarginInfo` decides.
 * `vatRate` is the product detail's VAT multiplier.
 */
export function usePdpMarginInfo(sku: string, vatRate: string | null): InfoPanelContent | null {
  const margin = useProductMargin(sku);
  const priced = shownEntry(usePriceEntry(sku));
  if (!realPricesEnabled() || priced === null) return null;
  if (margin.kind !== 'shown' && margin.kind !== 'unavailable') return null;
  return pdpMarginInfo(priced, vatRate);
}
